import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText, X, Loader2 } from "lucide-react";
import { uploadFile } from "@/lib/api";
import { toast } from "sonner";

export const FileDropzone = ({ value, valueName, onUploaded, testid = "file-dropzone" }) => {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const file = files?.[0];
      if (!file) return;
      const okExt = ["pdf", "png", "jpg", "jpeg", "docx", "xlsx"];
      const ext = file.name.split(".").pop().toLowerCase();
      if (!okExt.includes(ext)) {
        toast.error("Formato não suportado. Use PDF, PNG, JPG, DOCX ou XLSX.");
        return;
      }
      setBusy(true);
      try {
        const res = await uploadFile(file);
        onUploaded({ id: res.id, name: res.filename });
        toast.success("Arquivo enviado com sucesso");
      } catch (e) {
        toast.error("Falha no upload do arquivo");
      } finally {
        setBusy(false);
      }
    },
    [onUploaded]
  );

  if (value) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-xl border border-[#E2E8F0] bg-emerald-50/50 px-4 py-3"
        data-testid={`${testid}-selected`}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <FileText className="h-5 w-5 shrink-0 text-emerald-600" />
          <span className="truncate text-sm text-slate-700">{valueName || "Arquivo anexado"}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
            data-testid={`${testid}-replace`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Substituir arquivo
          </button>
          <button
            type="button"
            onClick={() => onUploaded({ id: null, name: null })}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            data-testid={`${testid}-remove`}
          >
            <X className="h-4 w-4" />
          </button>
          <input ref={inputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
            onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-replace-input`} />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
        dragging ? "border-[#134074] bg-slate-50" : "border-[#E2E8F0] bg-slate-50/50 hover:border-[#134074]"
      }`}
      data-testid={testid}
    >
      {busy ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#134074]" />
      ) : (
        <UploadCloud className="h-6 w-6 text-[#134074]" />
      )}
      <p className="text-sm font-medium text-slate-600">
        {busy ? "Enviando..." : "Arraste e solte ou clique para enviar"}
      </p>
      <p className="text-xs text-slate-400">PDF, PNG, JPG, DOCX, XLSX</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};
